import { useState, useRef, useEffect } from 'react';

interface PlaybackSpeedProps {
  rate: number;
  onRateChange: (rate: number) => void;
}

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

export function PlaybackSpeed({ rate, onRateChange }: PlaybackSpeedProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Format rate for display
  const formatRate = (r: number) => {
    if (r === 1) return '1x';
    return `${r}x`;
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Speed button/badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Playback speed: ${rate}x`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className="px-2.5 py-1 text-xs font-semibold tabular-nums
                   text-[var(--color-text-secondary)]
                   hover:text-[var(--color-text)]
                   bg-[var(--color-surface-elevated)]
                   hover:bg-[var(--color-border)]
                   rounded-full transition-colors duration-150
                   focus:outline-none focus-visible:ring-2
                   focus-visible:ring-[var(--color-primary)]"
      >
        {formatRate(rate)}
      </button>

      {/* Speed menu */}
      {isOpen && (
        <div
          role="listbox"
          aria-label="Select playback speed"
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2
                     py-1 bg-[var(--color-surface)] rounded-[var(--radius-lg)]
                     shadow-[var(--shadow-lg)] border border-[var(--color-border)]
                     min-w-[4rem] z-50"
        >
          {SPEEDS.map((speed) => (
            <button
              key={speed}
              role="option"
              aria-selected={rate === speed}
              onClick={() => {
                onRateChange(speed);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-1.5 text-xs font-medium tabular-nums text-center
                         transition-colors duration-100
                         focus:outline-none focus-visible:bg-[var(--color-border)]
                         ${rate === speed
                           ? 'text-[var(--color-primary)] bg-[var(--color-primary)]/10'
                           : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-elevated)]'
                         }`}
            >
              {formatRate(speed)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
